import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import { Loader2, Plus, Edit, Trash2, ArrowRight, Globe2, UserX, Send } from 'lucide-react';
import EmployeeSuggest from './EmployeeSuggest';
import { preventImplicitSubmit } from '../utils/form';

const emptyForm = {
  fromEmployee: null,
  toEmployee: null,
  reason: '',
  isActive: true,
  isGlobal: false,
  formIds: [],
  handOverActive: false,
  notifyReplacement: true
};

export default function AdminReplacements() {
  const [replacements, setReplacements] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formState, setFormState] = useState(emptyForm);
  const [handoverDetails, setHandoverDetails] = useState([]);
  const [applyingId, setApplyingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  });

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [replacementsRes, formsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/replacements`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/api/forms`, { headers: authHeaders() })
      ]);

      if (replacementsRes.ok) {
        setReplacements(await replacementsRes.json());
      } else {
        const data = await replacementsRes.json().catch(() => ({}));
        setError(data.message || 'Failed to load replacements');
        setReplacements([]);
      }
      if (formsRes.ok) setForms(await formsRes.json());
    } catch (e) {
      setError('Failed to load replacements');
    } finally {
      setLoading(false);
    }
  };

  const formNameById = useMemo(() => {
    const map = {};
    forms.forEach(f => { map[f.id] = f.name; });
    return map;
  }, [forms]);

  const filtered = replacements.filter(r => {
    const haystack = `${r.fromEmpCode} ${r.fromName || ''} ${r.toEmpCode} ${r.toName || ''} ${r.reason || ''}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  const resetForm = () => {
    setEditingId(null);
    setFormState(emptyForm);
    setError('');
    setNotice('');
    setHandoverDetails([]);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setFormState({
      fromEmployee: { Empcode: item.fromEmpCode, EmployeeName: item.fromName || item.fromEmpCode },
      toEmployee: { Empcode: item.toEmpCode, EmployeeName: item.toName || item.toEmpCode },
      reason: item.reason || '',
      isActive: item.isActive,
      isGlobal: item.isGlobal,
      formIds: item.formIds || [],
      handOverActive: false,
      notifyReplacement: true
    });
    setError('');
    setNotice('');
    setHandoverDetails([]);
  };

  const toggleForm = (formId) => {
    setFormState(prev => ({
      ...prev,
      formIds: prev.formIds.includes(formId)
        ? prev.formIds.filter(id => id !== formId)
        : [...prev.formIds, formId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');

    const fromEmpCode = formState.fromEmployee?.Empcode;
    const toEmpCode = formState.toEmployee?.Empcode;

    if (!fromEmpCode || !toEmpCode) {
      setError('Select both the employee being replaced and their replacement');
      return;
    }

    setSaving(true);
    try {
      const url = editingId
        ? `${API_BASE_URL}/api/replacements/${editingId}`
        : `${API_BASE_URL}/api/replacements`;

      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          fromEmpCode,
          toEmpCode,
          reason: formState.reason,
          isActive: formState.isActive,
          isGlobal: formState.isGlobal,
          formIds: formState.isGlobal ? [] : formState.formIds,
          handOverActive: formState.handOverActive,
          notifyReplacement: formState.notifyReplacement
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.message || 'Failed to save replacement');
        return;
      }

      const base = editingId ? 'Replacement updated' : 'Replacement created';
      const handover = data.handover;
      resetForm();
      setNotice(handover?.summary ? `${base}. ${handover.summary}` : base);
      setHandoverDetails(handover?.details || []);
      loadData();
    } catch (err) {
      setError('Failed to save replacement');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyToActive = async (item) => {
    const confirmed = window.confirm(
      `Hand the steps ${item.fromName || item.fromEmpCode} is currently sitting on over to ${item.toName || item.toEmpCode}, and email them about each one?`
    );
    if (!confirmed) return;

    setApplyingId(item.id);
    setError('');
    setNotice('');
    setHandoverDetails([]);
    try {
      const response = await fetch(`${API_BASE_URL}/api/replacements/${item.id}/apply`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ notifyReplacement: true })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.message || 'Failed to hand over in-flight requests');
        return;
      }
      setNotice(data.message || 'Handover complete');
      setHandoverDetails(data.handover?.details || []);
      loadData();
    } catch (e) {
      setError('Failed to hand over in-flight requests');
    } finally {
      setApplyingId(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove the replacement ${item.fromName || item.fromEmpCode} -> ${item.toName || item.toEmpCode}?`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/replacements/${item.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (response.ok) loadData();
    } catch (e) {
      setError('Failed to delete replacement');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Loader2 size={48} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="triggers-shell">
      <div className="triggers-header">
        <div>
          <h1>Approver Replacements</h1>
          <p className="dashboard-subtitle">
            Redirect an approver to someone else. The replaced person is never assigned and never notified.
          </p>
        </div>
        <button className="button-primary button-lg" onClick={resetForm}>
          <Plus size={16} style={{ marginRight: '4px' }} /> New Replacement
        </button>
      </div>

      {error && (
        <div style={{ margin: '0 0 16px', padding: '10px 12px', borderRadius: 6, background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: 13 }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ margin: '0 0 16px', padding: '10px 12px', borderRadius: 6, background: '#dcfce7', border: '1px solid #86efac', color: '#166534', fontSize: 13 }}>
          {notice}
        </div>
      )}
      {handoverDetails.length > 0 && (
        <div style={{ margin: '0 0 16px', padding: '10px 12px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 12 }}>
          <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>Handed-over steps</div>
          {handoverDetails.map((detail, index) => (
            <div key={`${detail.requestId}-${detail.nodeId}-${index}`} style={{ color: detail.sent ? '#15803d' : '#b45309', marginTop: 2 }}>
              {detail.requestId} - {detail.stageLabel || detail.nodeId}
              {detail.formName ? ` (${detail.formName})` : ''}
              {' - '}
              {detail.sent ? `emailed ${detail.recipient || 'the replacement'}` : (detail.reason || 'no email sent')}
            </div>
          ))}
        </div>
      )}

      <div className="triggers-layout">
        <div className="triggers-panel">
          <div className="dashboard-filters" style={{ marginBottom: 16 }}>
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, code or reason..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {filtered.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 24px' }}>
              <UserX size={44} />
              <h3>No replacements yet</h3>
              <p>Create one to redirect an approver on selected forms</p>
            </div>
          ) : (
            <div className="triggers-grid">
              {filtered.map(item => (
                <div key={item.id} className="trigger-card">
                  <div className="trigger-card-header">
                    <div className="trigger-card-title">
                      <span className="trigger-icon"><UserX size={18} /></span>
                      <div>
                        <div className="trigger-name" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                            {item.fromName || item.fromEmpCode}
                          </span>
                          <ArrowRight size={14} />
                          <span>{item.toName || item.toEmpCode}</span>
                        </div>
                        <div className="trigger-meta">{item.fromEmpCode} &rarr; {item.toEmpCode}</div>
                      </div>
                    </div>
                    <div className="trigger-actions">
                      <button
                        className="action-btn"
                        onClick={() => handleApplyToActive(item)}
                        disabled={!item.isActive || applyingId === item.id}
                        title={item.isActive ? 'Hand over in-flight requests and notify' : 'Activate this replacement first'}
                      >
                        {applyingId === item.id ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                      </button>
                      <button className="action-btn" onClick={() => startEdit(item)} title="Edit">
                        <Edit size={16} />
                      </button>
                      <button className="action-btn danger" onClick={() => handleDelete(item)} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="trigger-card-body">
                    <div className="trigger-badges">
                      <span className={`status-pill ${item.isActive ? 'active' : 'inactive'}`}>
                        {item.isActive ? 'Active' : 'Paused'}
                      </span>
                      <span className="status-pill">
                        <Globe2 size={12} style={{ marginRight: 6 }} /> {item.isGlobal ? 'All forms' : `${item.formIds.length} form(s)`}
                      </span>
                    </div>
                    {!item.isGlobal && item.formIds.length > 0 && (
                      <div className="trigger-forms">
                        {item.formIds.map(id => formNameById[id] || id).join(', ')}
                      </div>
                    )}
                    {item.reason && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{item.reason}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="triggers-editor">
          <div className="triggers-editor-card">
            <div className="triggers-editor-header">
              <div className="triggers-editor-title">{editingId ? 'Edit Replacement' : 'Create Replacement'}</div>
              {editingId && <button className="button-outline button-sm" onClick={resetForm}>New</button>}
            </div>
            <form onSubmit={handleSubmit} onKeyDown={preventImplicitSubmit} className="triggers-form">
              <div className="form-field">
                <label>Replace this approver</label>
                <EmployeeSuggest
                  value={formState.fromEmployee ? formState.fromEmployee.EmployeeName : ''}
                  onChange={(item) => setFormState(prev => ({ ...prev, fromEmployee: item }))}
                  placeholder="Name or employee code"
                />
                {formState.fromEmployee && (
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                    {formState.fromEmployee.EmployeeName} ({formState.fromEmployee.Empcode})
                    {formState.fromEmployee.DepartmentName ? ` - ${formState.fromEmployee.DepartmentName}` : ''}
                  </div>
                )}
              </div>

              <div className="form-field">
                <label>Redirect approvals to</label>
                <EmployeeSuggest
                  value={formState.toEmployee ? formState.toEmployee.EmployeeName : ''}
                  onChange={(item) => setFormState(prev => ({ ...prev, toEmployee: item }))}
                  placeholder="Name or employee code"
                />
                {formState.toEmployee && (
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                    {formState.toEmployee.EmployeeName} ({formState.toEmployee.Empcode})
                    {formState.toEmployee.DepartmentName ? ` - ${formState.toEmployee.DepartmentName}` : ''}
                  </div>
                )}
              </div>

              <div className="form-grid two-col">
                <div className="form-field">
                  <label>Status</label>
                  <div className="toggle-row">
                    <button
                      type="button"
                      className={`toggle-btn ${formState.isActive ? 'active' : ''}`}
                      onClick={() => setFormState(prev => ({ ...prev, isActive: true }))}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${!formState.isActive ? 'active' : ''}`}
                      onClick={() => setFormState(prev => ({ ...prev, isActive: false }))}
                    >
                      Paused
                    </button>
                  </div>
                </div>
                <div className="form-field">
                  <label>Scope</label>
                  <div className="toggle-row">
                    <button
                      type="button"
                      className={`toggle-btn ${formState.isGlobal ? 'active' : ''}`}
                      onClick={() => setFormState(prev => ({ ...prev, isGlobal: true, formIds: [] }))}
                    >
                      All Forms
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${!formState.isGlobal ? 'active' : ''}`}
                      onClick={() => setFormState(prev => ({ ...prev, isGlobal: false }))}
                    >
                      Selected Forms
                    </button>
                  </div>
                </div>
              </div>

              {!formState.isGlobal && (
                <div className="form-field">
                  <label>Apply To Forms</label>
                  <div className="pill-choices">
                    {forms.map(form => (
                      <button
                        key={form.id}
                        type="button"
                        className={formState.formIds.includes(form.id) ? 'pill active' : 'pill'}
                        onClick={() => toggleForm(form.id)}
                      >
                        {form.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-field">
                <label>In-flight requests</label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontWeight: 400, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formState.handOverActive}
                    onChange={(e) => setFormState(prev => ({ ...prev, handOverActive: e.target.checked }))}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    Hand over steps already waiting on this approver
                    <span style={{ display: 'block', color: '#64748b', fontSize: 12 }}>
                      Cancels their pending assignment and assigns the replacement instead. Without this, only steps reached from now on are redirected.
                    </span>
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontWeight: 400, fontSize: 13, marginTop: 8, color: formState.handOverActive ? '#334155' : '#94a3b8', cursor: formState.handOverActive ? 'pointer' : 'not-allowed' }}>
                  <input
                    type="checkbox"
                    checked={formState.notifyReplacement}
                    disabled={!formState.handOverActive}
                    onChange={(e) => setFormState(prev => ({ ...prev, notifyReplacement: e.target.checked }))}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    Email the replacement about each one
                    <span style={{ display: 'block', color: '#94a3b8', fontSize: 12 }}>
                      Sends the same request.assigned mail a normal step assignment sends.
                    </span>
                  </span>
                </label>
              </div>

              <div className="form-field">
                <label>Reason</label>
                <input
                  type="text"
                  value={formState.reason}
                  onChange={(e) => setFormState(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Optional, e.g. on long leave"
                />
              </div>

              <div className="triggers-footer">
                <button type="submit" className="button-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingId ? 'Update Replacement' : 'Create Replacement')}
                </button>
                <button type="button" className="button-outline" onClick={resetForm}>Clear</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
